import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Home, Paintbrush, Building2, Flame,
  Maximize2, Hammer, ArrowRight, Shield,
  ChevronRight, Check, Star, Phone,
  ShieldCheck, BadgeDollarSign, ClipboardCheck, MapPin, Quote
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";

const HERO_IMG = "https://images.pexels.com/photos/8134846/pexels-photo-8134846.jpeg?auto=compress&cs=tinysrgb&w=1600";
const WHY_US_IMG = "https://images.pexels.com/photos/15798782/pexels-photo-15798782.jpeg?auto=compress&cs=tinysrgb&w=800";

const services = [
  { name: "Roofing", icon: Home, desc: "Installation, repair & replacement", image: "https://images.pexels.com/photos/33404981/pexels-photo-33404981.jpeg?auto=compress&cs=tinysrgb&w=400" },
  { name: "Painting", icon: Paintbrush, desc: "Interior & exterior painting", image: "https://images.pexels.com/photos/36153946/pexels-photo-36153946.jpeg?auto=compress&cs=tinysrgb&w=400" },
  { name: "Window & Doors", icon: Maximize2, desc: "Professional installation", image: "https://images.unsplash.com/photo-1771968965646-9818a67c186a?w=400&auto=format" },
  { name: "Home Additions", icon: Building2, desc: "Custom home expansions", image: "https://images.unsplash.com/photo-1632143697739-5559b57ac985?w=400&auto=format" },
  { name: "Fire & Water Restoration", icon: Flame, desc: "Damage restoration services", image: "https://images.unsplash.com/photo-1632624759923-86919326bd0f?w=400&auto=format" },
  { name: "Bonus Rooms", icon: Hammer, desc: "Convert unused spaces", image: "https://images.unsplash.com/photo-1765959617888-30837a158667?w=400&auto=format" },
  { name: "Driveways", icon: ArrowRight, desc: "Durable driveway solutions", image: "https://images.pexels.com/photos/8134845/pexels-photo-8134845.jpeg?auto=compress&cs=tinysrgb&w=400" },
  { name: "Fences & More", icon: Shield, desc: "Custom fencing & exteriors", image: "https://images.unsplash.com/photo-1769831190663-95fe8454d8c9?w=400&auto=format" },
];

const whyUs = [
  "Licensed & Insured",
  "Free Estimates",
  "Financing Available",
  "Quality Craftsmanship",
  "Local Memphis Company",
  "Customer Satisfaction Guaranteed",
];

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={HERO_IMG}
            alt="Construction"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 hero-overlay" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-20 md:py-32">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-2xl"
          >
            <span className="font-outfit text-sm font-bold tracking-[0.2em] uppercase text-brand block mb-4">
              Professional General Contractor
            </span>
            <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase text-white mb-6 leading-[0.95]">
              Building Memphis
              <br />
              <span className="text-brand">with Star Quality</span>
            </h1>
            <p className="font-dm-sans text-base sm:text-lg text-neutral-300 mb-8 leading-relaxed">
              Roofing, Painting, Remodeling & Restoration — Trusted contractor
              serving Memphis and surrounding areas.
            </p>
            <div className="flex flex-wrap items-center gap-4 mb-8">
              <Badge
                data-testid="financing-badge"
                className="bg-brand text-white font-outfit font-bold uppercase tracking-wider text-xs px-4 py-2 rounded-none border-0 hover:bg-brand-hover"
              >
                <Star className="w-3 h-3 mr-1.5 fill-white" />
                Financing Available
              </Badge>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link to="/contact">
                <Button
                  data-testid="hero-cta-button"
                  className="bg-brand text-white font-outfit font-bold uppercase tracking-wider text-sm px-8 py-6 hover:bg-brand-hover rounded-none"
                >
                  Get a Free Quote
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
              <a href="tel:9014061803">
                <Button
                  variant="outline"
                  className="border-2 border-white text-white font-outfit font-bold uppercase tracking-wider text-sm px-8 py-6 hover:bg-white hover:text-brand-dark rounded-none bg-transparent"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  (901) 406-1803
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* TRUST BADGES */}
      <section className="py-6 bg-brand-dark">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-8">
            {[
              { icon: ShieldCheck, text: "Licensed & Insured" },
              { icon: ClipboardCheck, text: "Free Estimates" },
              { icon: BadgeDollarSign, text: "Financing Available" },
              { icon: MapPin, text: "Locally Owned" },
            ].map((badge, i) => {
              const Icon = badge.icon;
              return (
                <motion.div
                  key={badge.text}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.3, delay: i * 0.1 }}
                  data-testid="trust-badge"
                  className="flex items-center justify-center gap-3 py-3"
                >
                  <Icon className="w-6 h-6 text-brand flex-shrink-0" />
                  <span className="font-outfit font-bold text-white text-sm uppercase tracking-wider">{badge.text}</span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES PREVIEW */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-12 md:mb-16"
          >
            <span className="font-outfit text-sm font-bold tracking-[0.2em] uppercase text-brand block mb-3">
              What We Do
            </span>
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold tracking-tight uppercase text-brand-dark">
              Our Services
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 border border-neutral-200">
            {services.map((service, i) => {
              const serviceId = service.name.toLowerCase().replace(/[&\s]+/g, "-");
              return (
                <motion.div
                  key={service.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  data-testid="service-card"
                  className="relative p-6 border border-neutral-200 group hover:bg-neutral-50 transition-colors service-line cursor-pointer"
                >
                  <Link to={`/services#${serviceId}`} className="absolute inset-0 z-10" aria-label={`View ${service.name} service`} />
                  <div className="w-full h-32 mb-4 overflow-hidden">
                    <img
                      src={service.image}
                      alt={service.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <h3 className="font-outfit text-lg font-bold tracking-tight text-brand-dark mb-2">
                    {service.name}
                  </h3>
                  <p className="font-dm-sans text-sm text-neutral-500">
                    {service.desc}
                  </p>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="mt-10 text-center"
          >
            <Link
              to="/services"
              className="inline-flex items-center gap-2 font-outfit font-bold text-sm uppercase tracking-wider text-brand hover:text-brand-hover transition-colors"
            >
              View All Services
              <ChevronRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-20 md:py-32 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 md:gap-20 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="overflow-hidden"
            >
              <img
                src={WHY_US_IMG}
                alt="Construction work"
                className="w-full h-[400px] md:h-[500px] object-cover"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="font-outfit text-sm font-bold tracking-[0.2em] uppercase text-brand block mb-3">
                Why Choose Us
              </span>
              <h2 className="font-outfit text-3xl sm:text-4xl font-bold tracking-tight uppercase text-brand-dark mb-8">
                Memphis Trusts
                <br />
                Star Builders
              </h2>
              <p className="font-dm-sans text-base text-neutral-600 leading-relaxed mb-8">
                With years of experience serving the Memphis community, Mid
                America Star Builders delivers exceptional quality on every
                project. From small repairs to complete renovations, we treat
                every home like our own.
              </p>
              <div className="space-y-4">
                {whyUs.map((item, i) => (
                  <motion.div
                    key={item}
                    initial={{ opacity: 0, x: 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: i * 0.08 }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 bg-brand flex items-center justify-center flex-shrink-0">
                      <Check className="w-4 h-4 text-white" />
                    </div>
                    <span className="font-dm-sans font-medium text-brand-dark">
                      {item}
                    </span>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* BEFORE & AFTER */}
      <section className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <span className="font-outfit text-sm font-bold tracking-[0.2em] uppercase text-brand block mb-3">
              See the Difference
            </span>
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold tracking-tight uppercase text-brand-dark">
              Before & After
            </h2>
            <p className="font-dm-sans text-neutral-500 text-base mt-3 max-w-md mx-auto">
              Drag the slider to see our transformations
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
              <p className="font-outfit font-bold text-sm uppercase tracking-wider text-brand-dark mb-3">Roofing Project</p>
              <BeforeAfterSlider
                beforeImg="https://images.unsplash.com/photo-1742156695571-fcc4035d32e2?w=800&auto=format"
                afterImg="https://images.unsplash.com/photo-1755114203680-d39d95efa82c?w=800&auto=format"
              />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }}>
              <p className="font-outfit font-bold text-sm uppercase tracking-wider text-brand-dark mb-3">Home Renovation</p>
              <BeforeAfterSlider
                beforeImg="https://images.unsplash.com/photo-1632624759923-86919326bd0f?w=800&auto=format"
                afterImg="https://images.unsplash.com/photo-1723470916372-325154109b74?w=800&auto=format"
              />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.15 }}>
              <p className="font-outfit font-bold text-sm uppercase tracking-wider text-brand-dark mb-3">Painting Project</p>
              <BeforeAfterSlider
                beforeImg="https://images.unsplash.com/photo-1762438442625-9e2b85cbebcc?w=800&auto=format"
                afterImg="https://images.unsplash.com/photo-1612296350607-076d142d15cd?w=800&auto=format"
              />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }}>
              <p className="font-outfit font-bold text-sm uppercase tracking-wider text-brand-dark mb-3">Fence Installation</p>
              <BeforeAfterSlider
                beforeImg="https://images.unsplash.com/photo-1620635295109-172b6677cba3?w=800&auto=format"
                afterImg="https://images.unsplash.com/photo-1748908271592-d9d5690b288b?w=800&auto=format"
              />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.25 }}>
              <p className="font-outfit font-bold text-sm uppercase tracking-wider text-brand-dark mb-3">Driveway Replacement</p>
              <BeforeAfterSlider
                beforeImg="https://images.unsplash.com/photo-1676828060750-d97d8e7df615?w=800&auto=format"
                afterImg="https://images.unsplash.com/photo-1628744448839-170bf324f27e?w=800&auto=format"
              />
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.3 }}>
              <p className="font-outfit font-bold text-sm uppercase tracking-wider text-brand-dark mb-3">Window Upgrade</p>
              <BeforeAfterSlider
                beforeImg="https://images.pexels.com/photos/5691507/pexels-photo-5691507.jpeg?auto=compress&w=800"
                afterImg="https://images.unsplash.com/photo-1775359924001-9cf0e5d3adf5?w=800&auto=format"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 md:py-32 bg-brand-dark">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12 md:mb-16"
          >
            <span className="font-outfit text-sm font-bold tracking-[0.2em] uppercase text-brand block mb-3">
              What Our Clients Say
            </span>
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold tracking-tight uppercase text-white">
              Customer Reviews
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                name: "James W.",
                location: "Memphis, TN",
                text: "Mid America Star Builders did an incredible job on our roof replacement. They were professional, on time, and the quality of work exceeded our expectations. Highly recommend!",
                rating: 5,
                service: "Roofing",
              },
              {
                name: "Patricia M.",
                location: "Germantown, TN",
                text: "We hired them for interior and exterior painting. The transformation was amazing. Mario and his team were courteous and cleaned up everything when they were done.",
                rating: 5,
                service: "Painting",
              },
              {
                name: "Robert & Lisa T.",
                location: "Cordova, TN",
                text: "After water damage in our home, MASB handled the entire restoration. They made a stressful situation so much easier. Our home looks better than before the damage!",
                rating: 5,
                service: "Restoration",
              },
              {
                name: "Denise K.",
                location: "Bartlett, TN",
                text: "They built a beautiful bonus room over our garage. The craftsmanship is top-notch and they stayed within budget. Mario kept us informed throughout the entire process.",
                rating: 5,
                service: "Bonus Room",
              },
              {
                name: "Marcus J.",
                location: "Southaven, MS",
                text: "New driveway and fence look absolutely fantastic. The crew was hard-working and respectful of our property. Will definitely use them again for future projects.",
                rating: 5,
                service: "Driveways & Fences",
              },
              {
                name: "Angela S.",
                location: "Collierville, TN",
                text: "We had all our windows replaced and a new front door installed. The energy savings are already noticeable. Professional team from start to finish. Five stars!",
                rating: 5,
                service: "Windows & Doors",
              },
            ].map((review, i) => (
              <motion.div
                key={review.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                data-testid="testimonial-card"
                className="bg-white/5 border border-white/10 p-6 hover:bg-white/10 transition-colors"
              >
                <div className="flex gap-1 mb-3">
                  {Array.from({ length: review.rating }).map((_, j) => (
                    <Star key={j} className="w-4 h-4 text-brand fill-brand" />
                  ))}
                </div>
                <Quote className="w-8 h-8 text-brand/30 mb-2" />
                <p className="font-dm-sans text-sm text-neutral-300 leading-relaxed mb-4">
                  {review.text}
                </p>
                <div className="border-t border-white/10 pt-4">
                  <p className="font-outfit font-bold text-white text-sm">{review.name}</p>
                  <p className="font-dm-sans text-xs text-neutral-400">{review.location} — {review.service}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-brand-dark">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="font-outfit text-3xl sm:text-4xl font-bold tracking-tight uppercase text-white mb-4">
              Ready to Start Your Project?
            </h2>
            <p className="font-dm-sans text-neutral-400 text-base mb-10 max-w-lg mx-auto">
              Contact us today for a free estimate. No project is too big or
              small.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/contact">
                <Button className="bg-brand text-white font-outfit font-bold uppercase tracking-wider text-sm px-10 py-6 hover:bg-brand-hover rounded-none">
                  Get a Free Quote
                </Button>
              </Link>
              <a href="tel:9014061803">
                <Button
                  variant="outline"
                  className="border-2 border-white text-white font-outfit font-bold uppercase tracking-wider text-sm px-10 py-6 hover:bg-white hover:text-brand-dark rounded-none bg-transparent"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Call Now
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
